import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import SeoMeta from "../../../platform/components/common/seoMeta";
import ContactUsForm from "../../../platform/pages/business/contactUsForm";

const SHARE_PATH = "/resident-management";
const PRICING_PATH = "/biz/pricing?product=care";
const LOGIN_PATH = `/login?returnTo=${encodeURIComponent("/biz/resident")}`;

const CARE_SETTINGS = [
  "Adult Family Homes",
  "Assisted Living",
  "Memory Care",
  "Senior Living",
];

const OPERATIONS_ITEMS = [
  "Resident profiles, admissions, and daily care details in one workspace",
  "eMAR, medication schedules, and follow-up without splitting the workflow",
  "Notes, observations, documents, and history kept together for the team",
];

const RESIDENT_STEPS = [
  "Set up resident records and admission details.",
  "Track medications, notes, and day-to-day care work in one place.",
  "Review history, alerts, and records without chasing details across tools.",
] as const;

export default function ResidentManagementPublicLandingPage(): JSX.Element {
  const [demoOpen, setDemoOpen] = React.useState(false);

  return (
    <>
      <SeoMeta
        title="Resident Management & eMAR | Plany365"
        description="For care teams that need resident records, medications, care notes, documents, and eMAR workflows in one focused workspace."
        url={SHARE_PATH}
      />

      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #eef8f6 0%, #ffffff 40%, #f2f4ef 100%)",
          py: { xs: 4, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 5,
              border: "1px solid rgba(25,87,92,0.14)",
              backgroundColor: "rgba(255,255,255,0.94)",
              boxShadow: "0 28px 70px rgba(13,42,45,0.10)",
              mb: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background:
                  "radial-gradient(circle at top left, rgba(13,91,102,0.10), transparent 32%), radial-gradient(circle at bottom right, rgba(83,116,73,0.10), transparent 34%)",
              }}
            />

            <Grid container>
              <Grid item xs={12} md={7}>
                <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
                  <Stack spacing={2.2}>
                    <Typography
                      variant="overline"
                      sx={{
                        letterSpacing: "0.14em",
                        fontWeight: 800,
                        color: "#4d7c79",
                      }}
                    >
                      Resident Management And eMAR
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: { xs: "2rem", sm: "2.5rem", md: "3.1rem" },
                        lineHeight: 0.98,
                        fontWeight: 900,
                        color: "#10363d",
                        maxWidth: 650,
                      }}
                    >
                      Resident operations and eMAR that keep daily care work clear.
                    </Typography>

                    <Typography
                      sx={{
                        maxWidth: 620,
                        color: "#4e6f74",
                        fontSize: { xs: "1rem", md: "1.05rem" },
                        lineHeight: 1.68,
                      }}
                    >
                      Keep resident records, medications, notes, and documents in
                      one place so staff can work with more clarity.
                    </Typography>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {CARE_SETTINGS.map((item) => (
                        <Chip
                          key={item}
                          label={item}
                          sx={{
                            borderRadius: 999,
                            bgcolor: alpha("#0d5b66", 0.08),
                            color: "#10363d",
                            fontWeight: 700,
                          }}
                        />
                      ))}
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Button
                        type="button"
                        onClick={() => setDemoOpen(true)}
                        variant="contained"
                        size="large"
                        sx={{
                          borderRadius: 999,
                          px: 2.8,
                          py: 1.1,
                          fontWeight: 800,
                          textTransform: "none",
                          bgcolor: "#0d5b66",
                          boxShadow: "0 14px 32px rgba(13,91,102,0.18)",
                          "&:hover": { bgcolor: "#0b4d57" },
                        }}
                      >
                        Request a Demo
                      </Button>
                      <Button
                        component={RouterLink}
                        to={LOGIN_PATH}
                        variant="outlined"
                        size="large"
                        sx={{
                          borderRadius: 999,
                          px: 2.5,
                          py: 1.1,
                          fontWeight: 800,
                          textTransform: "none",
                          borderColor: "rgba(16,54,61,0.22)",
                          color: "#10363d",
                        }}
                      >
                        Open Resident Portal
                      </Button>
                      <Button
                        component={RouterLink}
                        to={PRICING_PATH}
                        variant="text"
                        size="large"
                        sx={{
                          borderRadius: 999,
                          px: 1.6,
                          py: 1.1,
                          fontWeight: 700,
                          textTransform: "none",
                          color: "#355a7d",
                        }}
                      >
                        View Pricing
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 }, height: "100%" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: { xs: 2.25, md: 2.75 },
                      borderRadius: 4,
                      border: "1px solid rgba(13,91,102,0.14)",
                      background:
                        "linear-gradient(180deg, rgba(13,91,102,0.06) 0%, rgba(255,255,255,0.95) 100%)",
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 800, color: "#0d5b66" }}
                      >
                        What teams get
                      </Typography>
                      <Typography
                        sx={{
                          color: "#10363d",
                          fontSize: { xs: 20, md: 23 },
                          fontWeight: 800,
                          lineHeight: 1.15,
                        }}
                      >
                        Keep resident records and daily care work aligned.
                      </Typography>

                      <Stack spacing={1}>
                        {OPERATIONS_ITEMS.map((item) => (
                          <Box
                            key={item}
                            sx={{ display: "flex", gap: 1.1, alignItems: "flex-start" }}
                          >
                            <Box
                              sx={{
                                mt: "8px",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "#0d5b66",
                                flexShrink: 0,
                              }}
                            />
                            <Typography sx={{ color: "#4e6f74", lineHeight: 1.65 }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: "#fff",
                          border: "1px solid rgba(13,91,102,0.12)",
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, color: "#10363d", mb: 0.75 }}>
                          Daily visibility
                        </Typography>
                        <Typography sx={{ color: "#4e6f74", lineHeight: 1.65 }}>
                          One place for meds, notes, alerts, and resident history.
                        </Typography>
                      </Paper>
                    </Stack>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid rgba(13,91,102,0.12)",
              boxShadow: "0 8px 20px rgba(13,42,45,0.04)",
              p: { xs: 2.5, md: 3.25 },
            }}
          >
            <Stack spacing={2}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: "0.12em", fontWeight: 800, color: "#4d7c79" }}
              >
                How it works
              </Typography>

              <Stack spacing={1.2}>
                {RESIDENT_STEPS.map((step, index) => (
                  <Box
                    key={step}
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      p: 1.35,
                      borderRadius: 3,
                      bgcolor: alpha("#0d5b66", 0.05),
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        bgcolor: "#0d5b66",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography sx={{ color: "#315e64", lineHeight: 1.65 }}>
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.1}
                useFlexGap
                flexWrap="wrap"
              >
                <Button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  variant="contained"
                  sx={{
                    alignSelf: "flex-start",
                    borderRadius: 999,
                    px: 2.2,
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: "#0d5b66",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#0b4d57", boxShadow: "none" },
                  }}
                >
                  Request a Demo
                </Button>
                <Button
                  component={RouterLink}
                  to={PRICING_PATH}
                  variant="text"
                  sx={{
                    alignSelf: "flex-start",
                    px: 0,
                    textTransform: "none",
                    fontWeight: 700,
                    color: "#22374a",
                  }}
                >
                  View pricing
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Dialog open={demoOpen} onClose={() => setDemoOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request a Demo</DialogTitle>
        <DialogContent dividers>
          <ContactUsForm
            onClose={() => setDemoOpen(false)}
            initialReason="demo"
            initialSubject="Request a Demo"
            initialMessage="I would like a demo of Plany365 Resident Management and eMAR for my care team. Please contact me to schedule a walkthrough."
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
